import PlatformPost from 'Modules/Plans/components/PlatformPosts/PlatformPost/PlatformPost';
import { mapTikTokPostToPlatformPostData } from 'Modules/Plans/components/PlatformPosts/PlatformPost/helpers';
import PostsGrid from 'Modules/Plans/components/PlatformPosts/PostsGrid/PostsGrid';
import useSinglePlayback from 'Hooks/vidstack/useSinglePlayback';
import PlatformPostsFilters, {
    Filters,
    CampaignOption,
} from 'Modules/Plans/components/PlatformPosts/PlatformPostsFilters/PlatformPostsFilters';
import { useState } from 'react';
import { useTiktokCampaigns } from '../../Campaigns/TiktokCampaign/useTiktokCampaigns';
import range from 'lodash/range';
import { MessageArea } from '@round/whitelabel-ui';
import styles from './TikTokTab.module.css';
import usePlatformPosts from 'Modules/Plans/hooks/queries/posts/usePlatformPosts';
import useOnScrolledPastThreshold from 'Hooks/scroll/useOnScrolledPastThreshold';
import { CountRangeLabel } from '@round/whitelabel-ui';

type Props = {
    campaignIds: number[];
    publicReportId: string | undefined;
};

const PAGE_SIZE = 50;

const TikTokPlatformPosts = ({ campaignIds, publicReportId }: Props) => {
    const [filters, setFilters] = useState<Filters>({});
    const { campaigns, brandsData, projectsData } = useTiktokCampaigns();
    const campaignOptions: CampaignOption[] = campaigns.map((c) => {
        const project = projectsData.get(c.project_id) ?? undefined;
        const brand = project ? brandsData[project?.brand_id]?.data ?? undefined : undefined;
        return {
            value: c.id,
            label: c.name,
            managedBy: c.managed_by,
            imageUrl: project?.image || '',
            brandName: brand?.name,
            projectName: project?.name,
        };
    });

    const { data, isLoading, errorMessage, status, userStatsCache, hasNextPage, isFetchingNextPage, fetchNextPage } =
        usePlatformPosts(
            {
                platform: 'tiktok',
                include_post_details: true,
                publicReportId,
                status: 'live',
                page_size: PAGE_SIZE,
                ...filters,
                campaign_id: filters.campaign_id || campaignIds.toString(),
            },
            // ensures it fetches only report related posts
            { enabled: Boolean(filters.campaign_id?.length || campaignIds.length) }
        );

    const posts = data?.pages.flatMap((p) => p.results) ?? [];
    const hasPosts = Boolean(status === 'success' && posts.length);
    const handleSinglePlayback = useSinglePlayback();
    const onEndReached = useOnScrolledPastThreshold({ threshold: 150 });

    return (
        <>
            <PlatformPostsFilters
                filters={filters}
                onChange={setFilters}
                campaignOptions={campaignOptions}
                publicReportId={publicReportId}
            />

            <CountRangeLabel
                className={styles.countRange}
                pageParams={data?.pageParams as number[]}
                count={data?.pages[0]?.count}
                pageSize={PAGE_SIZE}
                isLoading={isLoading}
            />

            {((!hasPosts && !isLoading) || !!errorMessage) && (
                <MessageArea appearance={errorMessage ? 'error' : undefined}>
                    <span>{errorMessage || 'No posts yet.'}</span>
                </MessageArea>
            )}

            <PostsGrid
                className={styles.postsGrid}
                onScroll={onEndReached(() => {
                    if (hasNextPage && !isFetchingNextPage && !isLoading) {
                        fetchNextPage();
                    }
                })}
            >
                {posts.map((post) => (
                    <PlatformPost
                        key={post.id}
                        data={mapTikTokPostToPlatformPostData(
                            post,
                            userStatsCache.find((c) => c.user_id === post.tiktok_details?.account_id)
                        )}
                        onPlay={handleSinglePlayback}
                    />
                ))}

                {(isLoading || isFetchingNextPage) &&
                    range(0, PAGE_SIZE).map((index) => <PlatformPost.LoadingView key={index} />)}
            </PostsGrid>
        </>
    );
};

export default TikTokPlatformPosts;
